// Bloc de code à COLORATION SYNTAXIQUE (fiches, scènes, user stories).
//
// Le rendu markdown maison des fiches ne connaissait pas les blocs ```lang : le code
// s'affichait à plat, sur une seule ligne, indistinct du texte. On s'appuie ici sur
// react-native-syntax-highlighter (enveloppe react-syntax-highlighter) : il rend des <Text>,
// donc il fonctionne à la fois sur le web et en natif.
import { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import SyntaxHighlighter from 'react-native-syntax-highlighter';
import { atomOneDark, atomOneLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { useT } from '../i18n/useT';

// Le langage annoncé par l'auteur (```py) n'est pas toujours celui de la lib (python).
const ALIASES: Record<string, string> = {
  py: 'python', js: 'javascript', ts: 'typescript', tsx: 'typescript', jsx: 'javascript',
  sh: 'bash', shell: 'bash', yml: 'yaml', ps: 'powershell', ps1: 'powershell',
};

export default function CodeBlock({ code, language, onOpenProject, accent }: {
  code: string; language?: string;
  // Si fourni (card/US portant une template), un bouton « Ouvrir l'IDE » apparaît dans la
  // barre du bloc → ouvre le projet dans l'éditeur.
  onOpenProject?: () => void; accent?: string;
}) {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const [copied, setCopied] = useState(false);
  const lang = ALIASES[(language || '').toLowerCase()] || (language || '').toLowerCase() || 'text';

  const copy = async () => {
    try {
      // Clipboard n'existe que sur le web ; en natif l'action est simplement absente.
      await (navigator as any)?.clipboard?.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* copie indisponible : on n'affiche pas d'erreur pour si peu */ }
  };

  return (
    <View style={[s.wrap, { borderColor: colors.border, backgroundColor: isDarkTheme ? '#282C34' : '#FAFAFA' }]}>
      <View style={[s.bar, { borderBottomColor: colors.border }]}>
        <Text style={[s.lang, { color: colors.textTertiary }]}>{lang}</Text>
        <View style={s.barActions}>
          {onOpenProject && (
            <TouchableOpacity onPress={onOpenProject} accessibilityLabel={t('code.openIdeWithProject')} style={s.ideBtn}>
              <Ionicons name="code-slash" size={12} color={accent || colors.primary} />
              <Text style={[s.copy, { color: accent || colors.primary }]}>{t('code.openIde')}</Text>
            </TouchableOpacity>
          )}
          {typeof navigator !== 'undefined' && (navigator as any)?.clipboard && (
            <TouchableOpacity onPress={copy} accessibilityLabel={t('code.copyCode')}>
              <Text style={[s.copy, { color: copied ? colors.primary : colors.textTertiary }]}>
                {t(copied ? 'code.copied' : 'code.copy')}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
      {/* Deux pièges cumulés, d'où les ascenseurs permanents :
          1. la lib rend PreTag/CodeTag en ScrollView par défaut et force `horizontal` ;
          2. surtout, le THÈME hljs porte `overflowX:'auto'` que la lib traduit en
             `overflow:'scroll'` (cf. generateNewStylesheet) — appliqué au PreTag, il affiche
             les deux barres en permanence, quelle que soit la balise.
          On neutralise donc les tags (View) ET l'overflow du thème (customStyle gagne, il est
          fusionné APRÈS la feuille de style). Le code se replie : pas de défilement imbriqué. */}
      <View style={s.code}>
        <SyntaxHighlighter
          language={lang}
          style={isDarkTheme ? atomOneDark : atomOneLight}
          highlighter="hljs"
          PreTag={View}
          CodeTag={View}
          customStyle={{ backgroundColor: 'transparent', padding: 0, margin: 0, overflow: 'visible' }}
          fontSize={13}
        >
          {code}
        </SyntaxHighlighter>
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  wrap: { borderWidth: 1, borderRadius: 10, overflow: 'hidden', marginVertical: 8 },
  bar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 10, paddingVertical: 5, borderBottomWidth: 1 },
  lang: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
  barActions: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  ideBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  copy: { fontSize: 11, fontWeight: '600' },
  code: { padding: 10, width: '100%' },
});
